import React, { useState } from "react";
import {
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Plus, X } from "@phosphor-icons/react";
import { ResponsiveModal } from "@answersnow/pkg-theme";
import { ASSESSMENT_TYPE_OPTIONS, CLINICAL_DOCUMENT_TYPE_OPTIONS } from "./constants";

interface DocumentRule {
  id?: number;
  clinical_document_type: string;
  required_assessments?: { operator: "AND" | "OR"; assessment_types: string[] } | null;
  requires_referring_provider: boolean;
  requires_criminal_legal_history: boolean;
  requires_drug_alcohol_use: boolean;
  requires_trauma_history: boolean;
}

const EMPTY_RULE: DocumentRule = {
  clinical_document_type: "",
  required_assessments: null,
  requires_referring_provider: false,
  requires_criminal_legal_history: false,
  requires_drug_alcohol_use: false,
  requires_trauma_history: false,
};

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (rule: DocumentRule) => void;
  existingRule?: DocumentRule | null;
  existingTypes?: string[];
  loading?: boolean;
}

export default function DocumentRulesDialog({
  open,
  onClose,
  onSave,
  existingRule,
  existingTypes = [],
  loading = false,
}: Props) {
  const [rule, setRule] = useState<DocumentRule>(existingRule ?? { ...EMPTY_RULE });

  React.useEffect(() => {
    if (open) {
      setRule(existingRule ?? { ...EMPTY_RULE });
    }
  }, [open, existingRule]);

  const availableTypes = CLINICAL_DOCUMENT_TYPE_OPTIONS.filter(
    (opt) =>
      !existingTypes.includes(opt.value) || opt.value === existingRule?.clinical_document_type
  );

  const assessmentTypes = rule.required_assessments?.assessment_types ?? [];
  const operator = rule.required_assessments?.operator ?? "AND";

  const handleAddAssessment = () => {
    const newTypes = [...assessmentTypes, ""];
    setRule({
      ...rule,
      required_assessments: { operator, assessment_types: newTypes },
    });
  };

  const handleRemoveAssessment = (index: number) => {
    const newTypes = assessmentTypes.filter((_, i) => i !== index);
    if (newTypes.length === 0) {
      setRule({ ...rule, required_assessments: null });
    } else {
      setRule({
        ...rule,
        required_assessments: { operator, assessment_types: newTypes },
      });
    }
  };

  const handleAssessmentChange = (index: number, value: string) => {
    const newTypes = [...assessmentTypes];
    newTypes[index] = value;
    setRule({
      ...rule,
      required_assessments: { operator, assessment_types: newTypes },
    });
  };

  const handleOperatorChange = (
    _: React.MouseEvent<HTMLElement>,
    newOperator: "AND" | "OR" | null
  ) => {
    if (!newOperator || !rule.required_assessments) return;
    setRule({
      ...rule,
      required_assessments: { ...rule.required_assessments, operator: newOperator },
    });
  };

  const handleSave = () => {
    // Filter out empty assessment selections
    const cleanedRule = { ...rule };
    if (cleanedRule.required_assessments) {
      const filteredTypes = cleanedRule.required_assessments.assessment_types.filter(
        (t) => t !== ""
      );
      if (filteredTypes.length === 0) {
        cleanedRule.required_assessments = null;
      } else {
        cleanedRule.required_assessments = {
          ...cleanedRule.required_assessments,
          assessment_types: filteredTypes,
        };
      }
    }
    onSave(cleanedRule);
  };

  const isEditing = !!existingRule?.id;
  const selectedTypes = assessmentTypes.filter((t) => t !== "");

  return (
    <ResponsiveModal
      open={open}
      onClose={onClose}
      title={isEditing ? "Edit Document Rules" : "Add Document Rules"}
      maxWidth="xs"
      dialogSx={{
        "& .MuiDialogContent-root > .MuiBox-root:first-of-type": { p: 0, pb: "16px" },
        "& .MuiIconButton-root > svg": { width: 20, height: 20 },
      }}
      dialogContentSx={{ p: 0 }}
      drawerSx={{ "& .MuiIconButton-root > svg": { width: 20, height: 20 } }}
      drawerContentSx={{ p: 0 }}
    >
      <Stack gap="24px" py="24px" pb="24px">
        {/* Clinical Document */}
        <Stack gap="8px">
          <Typography variant="caption" color="text.secondary">
            Clinical Document
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            value={rule.clinical_document_type}
            onChange={(e) => setRule({ ...rule, clinical_document_type: e.target.value })}
            disabled={isEditing}
            SelectProps={{
              displayEmpty: true,
              renderValue: (value: unknown) => {
                if (!value) return <Typography color="text.disabled">Select document</Typography>;
                return availableTypes.find((opt) => opt.value === value)?.label ?? value;
              },
            }}
          >
            {availableTypes.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        {/* Required assessments */}
        <Stack gap="16px">
          <Typography variant="subtitle2">Required assessments</Typography>
          <Stack gap="12px" pl="24px">
            {assessmentTypes.map((assessmentType, index) => (
              <React.Fragment key={index}>
                <Stack direction="row" gap="8px" alignItems="center">
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={assessmentType}
                    onChange={(e) => handleAssessmentChange(index, e.target.value)}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (value: unknown) => {
                        if (!value)
                          return <Typography color="text.disabled">Select assessment</Typography>;
                        return (
                          ASSESSMENT_TYPE_OPTIONS.find((opt) => opt.value === value)?.label ?? value
                        );
                      },
                    }}
                  >
                    {ASSESSMENT_TYPE_OPTIONS.filter(
                      (opt) => !selectedTypes.includes(opt.value) || opt.value === assessmentType
                    ).map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <IconButton
                    size="small"
                    aria-label="Remove assessment"
                    onClick={() => handleRemoveAssessment(index)}
                    sx={{ "& > svg": { width: 20, height: 20 } }}
                  >
                    <X size={20} style={{ width: 20, height: 20 }} />
                  </IconButton>
                </Stack>
                {index < assessmentTypes.length - 1 && (
                  <Stack direction="row" gap="12px" alignItems="center">
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={operator}
                      onChange={handleOperatorChange}
                    >
                      <ToggleButton value="AND" sx={{ px: 2, py: 1 }}>
                        AND
                      </ToggleButton>
                      <ToggleButton value="OR" sx={{ px: 2, py: 1 }}>
                        OR
                      </ToggleButton>
                    </ToggleButtonGroup>
                    <Typography variant="caption" color="text.secondary">
                      {operator === "AND"
                        ? "All assessments must be current"
                        : "At least one must be current"}
                    </Typography>
                  </Stack>
                )}
              </React.Fragment>
            ))}
            <Button
              size="small"
              variant="text"
              startIcon={<Plus size={16} />}
              onClick={handleAddAssessment}
              sx={{ alignSelf: "flex-start" }}
            >
              Add Assessment
            </Button>
          </Stack>
        </Stack>

        {/* Other requirements */}
        <Stack gap="16px">
          <Typography variant="subtitle2">Other requirements</Typography>
          <Stack gap="24px">
            <FormControlLabel
              control={
                <Checkbox
                  checked={rule.requires_referring_provider}
                  onChange={(e) =>
                    setRule({ ...rule, requires_referring_provider: e.target.checked })
                  }
                />
              }
              label="Payor requires Referring Provider listed"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={rule.requires_criminal_legal_history}
                  onChange={(e) =>
                    setRule({ ...rule, requires_criminal_legal_history: e.target.checked })
                  }
                />
              }
              label="Payor requires criminal and legal history reported"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={rule.requires_drug_alcohol_use}
                  onChange={(e) =>
                    setRule({ ...rule, requires_drug_alcohol_use: e.target.checked })
                  }
                />
              }
              label="Payor requires drug and alcohol use reported"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={rule.requires_trauma_history}
                  onChange={(e) => setRule({ ...rule, requires_trauma_history: e.target.checked })}
                />
              }
              label="Payor requires trauma history reported"
            />
          </Stack>
        </Stack>
      </Stack>
      {/* Actions */}
      <Stack direction="row" gap={2} justifyContent="flex-end" pt="16px">
        <Button variant="contained" color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!rule.clinical_document_type || loading}
        >
          {isEditing ? "Update Document Rules" : "Add Document Rules"}
        </Button>
      </Stack>
    </ResponsiveModal>
  );
}
